import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { SubscriptionBillingService } from '../../services/subscriptionBilling.service';
import { InstitutionService } from '../../services/institution.service';
import { ISubscriptionInvoice, InvoiceStatus, IInvoicePayment } from '../../interfaces/billing.interface';
import { IInstitution, IInstitutionResponse } from '../../interfaces/institution.interface';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import InvoiceCreateDialog from './InvoiceCreateDialog';
import MarkPaidDialog from './MarkPaidDialog';

interface IInstitutionWithEmployeeCount extends IInstitution {
    employeeCount?: number;
}

const formatAmount = (amount: number): string => `BDT ${amount.toFixed(2)}`;

const invoiceStatusColor = (status: InvoiceStatus): 'default' | 'success' | 'warning' | 'error' => {
    switch (status) {
        case 'PAID':
            return 'success';
        case 'ISSUED':
            return 'warning';
        case 'CANCELLED':
            return 'error';
        default:
            return 'default';
    }
};

const paymentStatusColor = (status: IInvoicePayment['status']): 'default' | 'success' | 'warning' | 'error' => {
    switch (status) {
        case 'COMPLETED':
            return 'success';
        case 'FAILED':
            return 'error';
        case 'REFUNDED':
            return 'default';
        default:
            return 'warning';
    }
};

const isStuckBkashPayment = (payment: IInvoicePayment): boolean =>
    payment.method === 'BKASH_GATEWAY' && (payment.status === 'INITIATED' || payment.status === 'EXECUTING');

const InvoiceRow: React.FC<{
    invoice: ISubscriptionInvoice;
    institutionName: string;
    onIssue: (invoice: ISubscriptionInvoice) => void;
    onCancel: (invoice: ISubscriptionInvoice) => void;
    onMarkPaid: (invoice: ISubscriptionInvoice) => void;
    onVerifyNow: (payment: IInvoicePayment) => void;
    onMarkRefunded: (payment: IInvoicePayment) => void;
}> = ({ invoice, institutionName, onIssue, onCancel, onMarkPaid, onVerifyNow, onMarkRefunded }) => {
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation('billing');

    return (
        <React.Fragment>
            <TableRow>
                <TableCell>
                    <IconButton
                        size="small"
                        aria-label={t('operatorBillingPage.togglePaymentHistory')}
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                    {invoice.invoiceNumber}
                </TableCell>
                <TableCell>{institutionName}</TableCell>
                <TableCell>
                    {invoice.periodStart} — {invoice.periodEnd}
                </TableCell>
                <TableCell>{formatAmount(invoice.amount)}</TableCell>
                <TableCell>
                    <Chip label={t(`invoiceStatus.${invoice.status}`)} color={invoiceStatusColor(invoice.status)} size="small" />
                </TableCell>
                <TableCell align="right">
                    {invoice.status === 'DRAFT' && (
                        <>
                            <Button size="small" onClick={() => onIssue(invoice)}>{t('operatorBillingPage.issue')}</Button>
                            <Button size="small" color="error" onClick={() => onCancel(invoice)}>{t('operatorBillingPage.cancel')}</Button>
                        </>
                    )}
                    {invoice.status === 'ISSUED' && (
                        <>
                            <Button size="small" onClick={() => onMarkPaid(invoice)}>{t('operatorBillingPage.markPaid')}</Button>
                            <Button size="small" color="error" onClick={() => onCancel(invoice)}>{t('operatorBillingPage.cancel')}</Button>
                        </>
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={6} sx={{ py: 0 }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2 }}>
                            {invoice.payments.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">{t('operatorBillingPage.noPaymentAttempts')}</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('columns.method')}</TableCell>
                                            <TableCell>{t('columns.amount')}</TableCell>
                                            <TableCell>{t('columns.status')}</TableCell>
                                            <TableCell>{t('columns.failureReason')}</TableCell>
                                            <TableCell align="right">{t('columns.action')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoice.payments.map(payment => (
                                            <TableRow key={payment.id}>
                                                <TableCell>{t(`paymentMethod.${payment.method}`)}</TableCell>
                                                <TableCell>{formatAmount(payment.amount)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={t(`paymentStatus.${payment.status}`)}
                                                        color={paymentStatusColor(payment.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>{payment.failureReason ?? '—'}</TableCell>
                                                <TableCell align="right">
                                                    {isStuckBkashPayment(payment) && (
                                                        <Button size="small" onClick={() => onVerifyNow(payment)}>
                                                            {t('operatorBillingPage.verifyNow')}
                                                        </Button>
                                                    )}
                                                    {payment.status === 'COMPLETED' && (
                                                        <Button size="small" onClick={() => onMarkRefunded(payment)}>
                                                            {t('operatorBillingPage.markRefunded')}
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const OperatorBillingPage: React.FC = () => {
    const { showSuccess, showApiError } = useToast();
    const { t } = useTranslation('billing');

    const [invoices, setInvoices] = useState<ISubscriptionInvoice[]>([]);
    const [institutions, setInstitutions] = useState<IInstitutionWithEmployeeCount[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const [institutionFilter, setInstitutionFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [markPaidInvoice, setMarkPaidInvoice] = useState<ISubscriptionInvoice | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const [invoicePage, institutionResponse] = await Promise.all([
                SubscriptionBillingService.getAllInvoices(),
                InstitutionService.getInstitutions(0, 100) as Promise<IInstitutionResponse>,
            ]);
            setInvoices(invoicePage.content);
            setInstitutions(institutionResponse.content as IInstitutionWithEmployeeCount[]);
        } catch (err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const institutionNameById = (id: string): string =>
        institutions.find(inst => inst.id === id)?.name ?? id;

    const filteredInvoices = invoices.filter(invoice => {
        if (institutionFilter && invoice.institutionId !== institutionFilter) return false;
        if (statusFilter && invoice.status !== statusFilter) return false;
        return true;
    });

    const handleIssue = async (invoice: ISubscriptionInvoice) => {
        try {
            await SubscriptionBillingService.issueInvoice(invoice.id);
            showSuccess(t('operatorBillingPage.issued'));
            await loadData();
        } catch (err) {
            showApiError(err, t('operatorBillingPage.issueFailed'));
        }
    };

    const handleCancel = async (invoice: ISubscriptionInvoice) => {
        try {
            await SubscriptionBillingService.cancelInvoice(invoice.id);
            showSuccess(t('operatorBillingPage.cancelled'));
            await loadData();
        } catch (err) {
            showApiError(err, t('operatorBillingPage.cancelFailed'));
        }
    };

    const handleVerifyNow = async (payment: IInvoicePayment) => {
        try {
            await SubscriptionBillingService.resolvePayment(payment.id);
            showSuccess(t('operatorBillingPage.paymentRefreshed'));
            await loadData();
        } catch (err) {
            showApiError(err, t('operatorBillingPage.verifyFailed'));
        }
    };

    const handleMarkRefunded = async (payment: IInvoicePayment) => {
        try {
            await SubscriptionBillingService.markRefunded(payment.id);
            showSuccess(t('operatorBillingPage.markedRefunded'));
            await loadData();
        } catch (err) {
            showApiError(err, t('operatorBillingPage.markRefundedFailed'));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('operatorBillingPage.title')}</Typography>
                <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>{t('operatorBillingPage.createInvoice')}</Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl sx={{ minWidth: 220 }} size="small">
                    <InputLabel id="institution-filter-label">{t('operatorBillingPage.institutionLabel')}</InputLabel>
                    <Select
                        labelId="institution-filter-label"
                        label={t('operatorBillingPage.institutionLabel')}
                        value={institutionFilter}
                        onChange={(e) => setInstitutionFilter(e.target.value)}
                        inputProps={{ 'aria-label': t('operatorBillingPage.institutionFilterAria') }}
                    >
                        <MenuItem value="">{t('operatorBillingPage.allInstitutions')}</MenuItem>
                        {institutions.map(inst => (
                            <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 180 }} size="small">
                    <InputLabel id="status-filter-label">{t('operatorBillingPage.statusLabel')}</InputLabel>
                    <Select
                        labelId="status-filter-label"
                        label={t('operatorBillingPage.statusLabel')}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        inputProps={{ 'aria-label': t('operatorBillingPage.statusFilterAria') }}
                    >
                        <MenuItem value="">{t('operatorBillingPage.allStatuses')}</MenuItem>
                        <MenuItem value="DRAFT">{t('invoiceStatus.DRAFT')}</MenuItem>
                        <MenuItem value="ISSUED">{t('invoiceStatus.ISSUED')}</MenuItem>
                        <MenuItem value="PAID">{t('invoiceStatus.PAID')}</MenuItem>
                        <MenuItem value="CANCELLED">{t('invoiceStatus.CANCELLED')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && error && (
                <Typography color="error">{t('operatorBillingPage.loadFailed')}</Typography>
            )}

            {!loading && !error && filteredInvoices.length === 0 && (
                <Typography color="text.secondary">{t('operatorBillingPage.empty')}</Typography>
            )}

            {!loading && !error && filteredInvoices.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('columns.invoiceNumber')}</TableCell>
                                <TableCell>{t('columns.institution')}</TableCell>
                                <TableCell>{t('columns.period')}</TableCell>
                                <TableCell>{t('columns.amount')}</TableCell>
                                <TableCell>{t('columns.status')}</TableCell>
                                <TableCell align="right">{t('columns.action')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredInvoices.map(invoice => (
                                <InvoiceRow
                                    key={invoice.id}
                                    invoice={invoice}
                                    institutionName={institutionNameById(invoice.institutionId)}
                                    onIssue={handleIssue}
                                    onCancel={handleCancel}
                                    onMarkPaid={setMarkPaidInvoice}
                                    onVerifyNow={handleVerifyNow}
                                    onMarkRefunded={handleMarkRefunded}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <InvoiceCreateDialog
                open={createDialogOpen}
                institutions={institutions}
                onClose={() => setCreateDialogOpen(false)}
                onSuccess={loadData}
            />

            <MarkPaidDialog
                open={!!markPaidInvoice}
                invoice={markPaidInvoice}
                onClose={() => setMarkPaidInvoice(null)}
                onSuccess={loadData}
            />
        </Box>
    );
};

export default OperatorBillingPage;
